import is from '@sindresorhus/is';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import type { FunctionComponent, ReactElement } from 'react';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useMediaQuery } from 'usehooks-ts';

import { useScrollFade } from '../../core/scroll-fade/ScrollFade';
import { LARGE_SCREEN_BREAKPOINT, MAIN_TILE_ID, TILE_REFLOW_DURATION } from './constants';
import {
    MainWidthLockstepProvider,
    TileErrorBoundaryProvider,
    TileWidthProvider,
    TilingEngineProvider,
    useTileResizeContext,
    useTilingEngineContext,
} from './TilingEngineContext';
import { TilingEngineMain } from './TilingEngineMain';
import { TilingEnginePanel } from './TilingEnginePanel';
import { TilingEnginePortal } from './TilingEnginePortal';
import { TilingEngineRightDock } from './TilingEngineRightDock';
import { resizedWidthCssValue, tileWidthVar } from './tile-resize';
import type {
    TileKind,
    TilingEngineMainProps,
    TilingEnginePanelProps,
    TilingEngineRightDockProps,
    TilingEngineRootProps,
} from './types';
import { useDragToScroll } from './use-drag-to-scroll';
import { useTileWidths } from './use-tile-widths';

const MS_PER_SECOND = 1000;

// Docks that stayed mounted but changed width, e.g. the chat dock growing when its threads rail opens.
const getResizedDockIds = (
    previousWidths: ReadonlyMap<string, string | undefined>,
    currentWidths: ReadonlyMap<string, string | undefined>,
): readonly string[] =>
    [...currentWidths.keys()].filter(
        (id) => previousWidths.has(id) && previousWidths.get(id) !== currentWidths.get(id),
    );

type TileDescriptor = {
    readonly autoScrollKey?: string;
    readonly holdAutoScroll?: boolean;
    readonly id: string;
    readonly kind?: TileKind;
    readonly minWidth?: string;
    readonly maxWidth?: string;
    readonly resizeKey?: string;
    readonly width?: string;
};

export type TileAutoScrollState = {
    readonly autoScrollKeys: ReadonlyMap<string, string | undefined>;
    readonly order: readonly string[];
};

const createTileAutoScrollState = (
    tileDescriptors: readonly TileDescriptor[],
    tileOrder: readonly string[],
): TileAutoScrollState => ({
    autoScrollKeys: new Map(tileDescriptors.map((tile) => [tile.id, tile.autoScrollKey] as const)),
    order: tileOrder,
});

const last = (ids: readonly string[]): string | undefined => ids[ids.length - 1];

export const getAutoScrollTargetId = (
    previous: TileAutoScrollState,
    current: TileAutoScrollState,
): string | undefined => {
    const previousSet = new Set(previous.order);
    const addedIds = current.order.filter((id) => !previousSet.has(id));
    const newestAddedId = last(addedIds);

    if (newestAddedId !== undefined) {
        return newestAddedId;
    }

    const currentSet = new Set(current.order);
    const removedIds = previous.order.filter((id) => !currentSet.has(id));

    if (removedIds.length > 0) {
        return last(current.order);
    }

    const changedIds = current.order.filter((id) => {
        if (!previousSet.has(id)) {
            return false;
        }

        const currentKey = current.autoScrollKeys.get(id);

        return currentKey !== undefined && previous.autoScrollKeys.get(id) !== currentKey;
    });

    return last(changedIds);
};

// A freshly opened or re-targeted tile outranks the held tile unless the held tile sits beyond it:
// parking the farther tile at the right edge keeps both in view. The hold alone steadies the view
// when a neighbour closes.
export const resolveAutoScrollTileId = (
    previous: TileAutoScrollState,
    current: TileAutoScrollState,
    targetId: string,
    holdId: string | undefined,
): string => {
    if (holdId === undefined) {
        return targetId;
    }

    const isFreshTarget =
        !previous.order.includes(targetId) ||
        previous.autoScrollKeys.get(targetId) !== current.autoScrollKeys.get(targetId);

    if (!isFreshTarget) {
        return holdId;
    }

    return current.order.indexOf(holdId) > current.order.indexOf(targetId) ? holdId : targetId;
};

const TilingEngineRootInner: FunctionComponent<TilingEngineRootProps> = ({
    autoScrollOnChange = 'newest',
    children,
    className,
    firstPanelWidth,
    offsetTop,
    style,
    tileErrorBoundary,
}) => {
    const { allTileIdsRef, containerRef, notify, registeredTiles, scrollCoordinator, setTileOrder, tileOrder } =
        useTilingEngineContext();
    const { resizedWidths } = useTileResizeContext();
    const { containerProps, isDragging } = useDragToScroll();
    const horizontalScrollFade = useScrollFade({
        edgeFadeSize: 'var(--spacing-4xl)',
        orientation: 'horizontal',
        viewportRef: containerRef,
    });
    const setContainerRef = useCallback(
        (node: HTMLDivElement | null): void => {
            horizontalScrollFade.viewportRef(node);
            containerProps.ref.current = node;
        },
        [containerProps.ref, horizontalScrollFade.viewportRef],
    );
    const isLargeScreen = useMediaQuery(LARGE_SCREEN_BREAKPOINT);

    const childArray = React.Children.toArray(children);

    const mainChild = childArray.find(
        (child): child is ReactElement<TilingEngineMainProps> =>
            React.isValidElement(child) && child.type === TilingEngineMain,
    );

    const panelChildren = childArray.filter(
        (child): child is ReactElement<TilingEnginePanelProps> =>
            React.isValidElement(child) && child.type === TilingEnginePanel,
    );

    const dockChildren = childArray.filter(
        (child): child is ReactElement<TilingEngineRightDockProps> =>
            React.isValidElement(child) && child.type === TilingEngineRightDock,
    );

    const panelKey = panelChildren
        .map((p) => `${p.props.id}:${p.props.width}:${p.props.minWidth}:${p.props.maxWidth}:${p.props.resizeKey}`)
        .join('|');

    const dockKey = dockChildren
        .map((d) => `${d.props.id}:${d.props.width}:${d.props.minWidth}:${d.props.maxWidth}`)
        .join('|');

    // biome-ignore lint/correctness/useExhaustiveDependencies: panelKey/dockKey are stable serializations of the prop sets — used instead of unstable array references
    const tileDescriptors = useMemo<readonly TileDescriptor[]>(() => {
        const mainDescriptor: TileDescriptor = {
            id: MAIN_TILE_ID,
            maxWidth: mainChild?.props.maxWidth,
            minWidth: mainChild?.props.minWidth,
        };

        const declarativePanels: readonly TileDescriptor[] = panelChildren.map((panel) => ({
            id: panel.props.id,
            kind: 'panel',
            maxWidth: panel.props.maxWidth,
            minWidth: panel.props.minWidth,
            resizeKey: panel.props.resizeKey,
            width: panel.props.width,
        }));

        const imperativeTiles: readonly TileDescriptor[] = registeredTiles.map((tile) => ({
            autoScrollKey: tile.autoScrollKey,
            holdAutoScroll: tile.holdAutoScroll,
            id: tile.id,
            kind: tile.kind ?? 'panel',
            maxWidth: tile.maxWidth,
            minWidth: tile.minWidth,
            resizeKey: tile.resizeKey,
            width: tile.width,
        }));

        const declarativeDocks: readonly TileDescriptor[] = dockChildren.map((dock) => ({
            id: dock.props.id,
            kind: 'dock',
            maxWidth: dock.props.maxWidth,
            minWidth: dock.props.minWidth,
            width: dock.props.width,
        }));

        return [mainDescriptor, ...declarativePanels, ...imperativeTiles, ...declarativeDocks];
    }, [mainChild?.props.maxWidth, mainChild?.props.minWidth, panelKey, dockKey, registeredTiles]);

    const tileWidths = useTileWidths(tileDescriptors, isLargeScreen, firstPanelWidth);
    const resizedWidthStyle = useMemo(
        () =>
            Object.fromEntries(
                [...resizedWidths].map(([resizeKey, width]) => [tileWidthVar(resizeKey), resizedWidthCssValue(width)]),
            ),
        [resizedWidths],
    );

    // A dock resizing in place (same tiles, new dock width — e.g. the chat dock toggling wide)
    // must move Main in lockstep with the dock's width transition; any other layout change keeps
    // Main's deliberate snap. Decided during render — not in an effect — so the transition rides
    // the same commit as the width change: layout reads elsewhere flush styles between commits,
    // and a transition enabled one commit late misses the first resize. The refs are only
    // mutated when the keys actually change, so re-renders (incl. StrictMode) are idempotent.
    const dockIdsKey = dockChildren.map((dock) => dock.props.id).join('|');
    const surroundingLayoutKey = [
        panelKey,
        registeredTiles.map((tile) => `${tile.id}:${tile.width}:${tile.minWidth}:${tile.maxWidth}`).join('|'),
    ].join('#');
    const isMainWidthLockstepRef = useRef(false);
    const previousLayoutKeysRef = useRef({ dockIdsKey, dockKey, surroundingLayoutKey });
    const previousLayoutKeys = previousLayoutKeysRef.current;
    if (previousLayoutKeys.dockKey !== dockKey || previousLayoutKeys.surroundingLayoutKey !== surroundingLayoutKey) {
        isMainWidthLockstepRef.current =
            previousLayoutKeys.surroundingLayoutKey === surroundingLayoutKey &&
            previousLayoutKeys.dockIdsKey === dockIdsKey;
        previousLayoutKeysRef.current = { dockIdsKey, dockKey, surroundingLayoutKey };
    }
    const isMainWidthLockstep = isMainWidthLockstepRef.current;

    const tileKindByIdRef = useRef<Map<string, TileKind>>(new Map());

    const dockWidthsKey = dockChildren.map((dock) => `${dock.props.id}:${dock.props.width}`).join('|');
    const dockWidthsRef = useRef<ReadonlyMap<string, string | undefined>>(
        new Map(dockChildren.map((dock) => [dock.props.id, dock.props.width] as const)),
    );

    useLayoutEffect(() => {
        const order = tileDescriptors.map((t) => t.id);
        allTileIdsRef.current = order;
        setTileOrder(order);

        const kindMap = new Map<string, TileKind>();
        for (const descriptor of tileDescriptors) {
            if (descriptor.kind !== undefined) {
                kindMap.set(descriptor.id, descriptor.kind);
            }
        }
        tileKindByIdRef.current = kindMap;
    }, [tileDescriptors, allTileIdsRef, setTileOrder]);

    const didEmitInitialSettled = useRef(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: refs should not go to depenencies.
    useEffect(() => {
        if (didEmitInitialSettled.current) {
            return;
        }

        didEmitInitialSettled.current = true;

        if (panelChildren.length > 0 || registeredTiles.length > 0 || dockChildren.length > 0) {
            notify({
                type: 'layoutSettled',
                tileIds: allTileIdsRef.current,
            });
        }
    }, []);

    const previousTileStateRef = useRef<TileAutoScrollState>({
        autoScrollKeys: new Map(),
        order: tileOrder,
    });
    const isInitialOrderRef = useRef(true);

    // When a sticky-right dock is present, the correct "end" position for the
    // newest tile is the surface's natural rightmost rest (`scrollWidth -
    // clientWidth`). `scrollToTile(id, 'end')` can't compute that landing site
    // because:
    //   - sticky tiles never move with scrollLeft, so their getBoundingClientRect
    //     is identical at every scroll position (the dock-id case is a no-op);
    //   - for a non-dock newest tile, the formula doesn't see the container's
    //     padding-right or the flex `gap-m` between panel and dock, so the
    //     tile lands flush against (or behind) the dock.
    // `scrollToEnd` clamps to scrollLeftMax, which the browser computes
    // including all paddings + gaps — exactly the visual layout we want.
    const scrollToRightEdgeFor = (id: string): void => {
        const hasDock = Array.from(tileKindByIdRef.current.values()).includes('dock');

        if (hasDock) {
            void scrollCoordinator.scrollToEnd();
            return;
        }

        void scrollCoordinator.scrollToTile(id, { align: 'end' });
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: scrollToRightEdgeFor reads a ref; intentional stable dep set.
    useEffect(() => {
        const previous = previousTileStateRef.current;
        const current = createTileAutoScrollState(tileDescriptors, tileOrder);
        previousTileStateRef.current = current;

        if (autoScrollOnChange === 'none') {
            isInitialOrderRef.current = false;
            return;
        }

        const holdId = tileDescriptors.find((tile) => tile.holdAutoScroll === true)?.id;

        if (isInitialOrderRef.current) {
            isInitialOrderRef.current = false;

            if (tileOrder.length > 1) {
                const last = tileOrder[tileOrder.length - 1];

                if (last !== undefined) {
                    scrollToRightEdgeFor(holdId ?? last);
                }
            }

            return;
        }

        const targetId = getAutoScrollTargetId(previous, current);

        if (targetId !== undefined) {
            scrollToRightEdgeFor(resolveAutoScrollTileId(previous, current, targetId, holdId));
        }
    }, [tileOrder, tileDescriptors, autoScrollOnChange, scrollCoordinator]);

    // A sticky dock that grows in place covers more of the tiles to its left. Once its width transition
    // has run, park the surface at its right edge again, the rest a newly opened or closed tile also
    // gets, so those tiles slide out from under the dock instead of staying covered. The scroll waits
    // for the dock's own transitionend; the timeout only covers a transition that never fires one
    // (reduced motion, or a width that resolves to the same pixels).
    // biome-ignore lint/correctness/useExhaustiveDependencies: dockWidthsKey is the stable serialization of the dock widths read from dockChildren.
    useEffect(() => {
        const previousWidths = dockWidthsRef.current;
        const currentWidths = new Map(dockChildren.map((dock) => [dock.props.id, dock.props.width] as const));
        dockWidthsRef.current = currentWidths;

        const resizedDockIds = getResizedDockIds(previousWidths, currentWidths);
        const container = containerRef.current;

        if (autoScrollOnChange === 'none' || resizedDockIds.length === 0 || is.null(container)) {
            return;
        }

        let hasScrolled = false;
        const scrollToRest = (): void => {
            if (hasScrolled) {
                return;
            }

            hasScrolled = true;
            container.removeEventListener('transitionend', handleTransitionEnd);
            clearTimeout(fallbackId);
            void scrollCoordinator.scrollToEnd();
        };
        const handleTransitionEnd = (event: TransitionEvent): void => {
            const isResizedDock =
                event.target instanceof HTMLElement &&
                resizedDockIds.includes(event.target.getAttribute('data-tile-id') ?? '');

            if (isResizedDock && event.propertyName === 'width') {
                scrollToRest();
            }
        };

        container.addEventListener('transitionend', handleTransitionEnd);
        const fallbackId = setTimeout(scrollToRest, TILE_REFLOW_DURATION * MS_PER_SECOND * 2);

        return () => {
            container.removeEventListener('transitionend', handleTransitionEnd);
            clearTimeout(fallbackId);
        };
    }, [dockWidthsKey, autoScrollOnChange, containerRef, scrollCoordinator]);

    const imperativePanels = registeredTiles
        .filter((tile) => tile.kind !== 'dock')
        .map((tile) => (
            <TilingEnginePanel
                key={tile.id}
                id={tile.id}
                maxWidth={tile.maxWidth}
                minWidth={tile.minWidth}
                resizeKey={tile.resizeKey}
                width={tile.width}
                zIndex={tile.zIndex}
            >
                {tile.content}
            </TilingEnginePanel>
        ));

    const imperativeDocks = registeredTiles
        .filter((tile) => tile.kind === 'dock')
        .map((tile) => (
            <TilingEngineRightDock
                key={tile.id}
                id={tile.id}
                maxWidth={tile.maxWidth}
                minWidth={tile.minWidth}
                width={tile.width}
            >
                {tile.content}
            </TilingEngineRightDock>
        ));

    const keyedPanelChildren = panelChildren.map((panel) => React.cloneElement(panel, { key: panel.props.id }));
    const keyedDockChildren = dockChildren.map((dock) => React.cloneElement(dock, { key: dock.props.id }));

    return (
        <TileWidthProvider value={tileWidths}>
            <MainWidthLockstepProvider value={isMainWidthLockstep}>
                <TileErrorBoundaryProvider value={tileErrorBoundary ?? null}>
                    <motion.div
                        {...containerProps}
                        data-overflow-left={horizontalScrollFade.overflowEdges.start}
                        data-overflow-right={horizontalScrollFade.overflowEdges.end}
                        ref={setContainerRef}
                        className={clsx(
                            'flex flex-row gap-m',
                            'overflow-x-auto overflow-y-hidden',
                            '[scrollbar-width:none]',
                            '[&[data-tile-resizing]>*]:!transition-none',
                            className,
                        )}
                        style={{
                            cursor: isDragging ? 'grabbing' : undefined,
                            height: `calc(100vh - ${offsetTop}px)`,
                            ...style,
                            ...horizontalScrollFade.style,
                            ...resizedWidthStyle,
                        }}
                    >
                        {mainChild}
                        <AnimatePresence initial={false}>
                            {keyedPanelChildren}
                            {imperativePanels}
                            {imperativeDocks}
                            {keyedDockChildren}
                        </AnimatePresence>
                    </motion.div>
                </TileErrorBoundaryProvider>
            </MainWidthLockstepProvider>
        </TileWidthProvider>
    );
};

const TilingEngineRoot: FunctionComponent<TilingEngineRootProps> = (props) => (
    <TilingEngineProvider>
        <TilingEngineRootInner {...props} />
    </TilingEngineProvider>
);

const TilingEngineSurface = TilingEngineRootInner;

export const TilingEngine = {
    Main: TilingEngineMain,
    Panel: TilingEnginePanel,
    Portal: TilingEnginePortal,
    Provider: TilingEngineProvider,
    RightDock: TilingEngineRightDock,
    Root: TilingEngineRoot,
    Surface: TilingEngineSurface,
} as const;
